import React, { useState, useEffect } from "react";
import { ThemeProvider } from "styled-components";
import { light } from "../styles/Themes";
import PfpGenerator from "../components/PfpGenerator";
import GlobalStyles from "../styles/GlobalStyles";
import styled from "styled-components";

const AccessContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 2rem;
  position: relative;
  color: ${(props) => props.theme.text};

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: url("/code-bg.jpg");
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    opacity: 0.8;
    z-index: -2;
  }

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${(props) => props.theme.body};
    opacity: 0.7;
    z-index: -1;
  }
`;

const AccessCard = styled.div`
  background: rgba(255, 255, 255, 0.9);
  padding: 2.5rem;
  border-radius: 1rem;
  box-shadow: 0 8px 32px rgba(31, 38, 135, 0.15);
  width: 100%;
  max-width: 400px;
  text-align: center;
  border: 1px solid ${(props) => props.theme.text}10;
`;

const Title = styled.h2`
  margin-bottom: 1.5rem;
  font-size: 1.8rem;
  color: ${(props) => props.theme.text};
  font-weight: 600;
`;

const CodeInput = styled.input`
  width: 100%;
  padding: 1rem;
  margin: 1rem 0;
  border: 2px solid ${(props) => props.theme.text}40;
  border-radius: 0.5rem;
  background: ${(props) => props.theme.body};
  color: ${(props) => props.theme.text};
  font-size: 1.2rem;
  text-align: center;
  letter-spacing: 0.2rem;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.text};
    background: ${(props) => props.theme.body};
  }

  &::placeholder {
    color: ${(props) => props.theme.text}40;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 1rem;
  border: none;
  border-radius: 0.5rem;
  background: ${(props) => props.theme.text};
  color: ${(props) => props.theme.body};
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 600;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.p`
  color: #ff6b6b;
  margin-top: 1rem;
  font-size: 0.9rem;
`;

const InfoText = styled.p`
  color: ${(props) => props.theme.text}80;
  margin-top: 1rem;
  font-size: 0.9rem;
`;

const PfpGeneratorPage = () => {
  const [hasAccess, setHasAccess] = useState(false);
  const [accessCode, setAccessCode] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const storedCode = localStorage.getItem("pfp_access_code");
    if (storedCode) {
      setHasAccess(true);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!accessCode.trim()) {
      setError("Please enter an access code");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const response = await fetch("/api/access/validate", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ code: accessCode }),
      });

      const data = await response.json();

      if (data.valid) {
        localStorage.setItem("pfp_access_code", accessCode);
        setHasAccess(true);
      } else {
        setError("Invalid or used access code");
      }
    } catch (err) {
      setError("Verification failed. Please try again later");
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSubmit(e);
    }
  };

  if (!hasAccess) {
    return (
      <>
        <GlobalStyles />
        <ThemeProvider theme={light}>
          <AccessContainer>
            <AccessCard>
              <Title>Greal</Title>
              <form onSubmit={handleSubmit}>
                <CodeInput
                  type="text"
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
                  placeholder="Enter Access Code"
                  maxLength={4}
                  onKeyPress={handleKeyPress}
                  disabled={isLoading}
                />
                <SubmitButton type="submit" disabled={isLoading}>
                  {isLoading ? "Verifying..." : "Enter"}
                </SubmitButton>
                {error && <ErrorMessage>{error}</ErrorMessage>}
                <InfoText>Please enter your 4-digit access code</InfoText>
              </form>
            </AccessCard>
          </AccessContainer>
        </ThemeProvider>
      </>
    );
  }

  return (
    <>
      <GlobalStyles />
      <ThemeProvider theme={light}>
        <PfpGenerator />
      </ThemeProvider>
    </>
  );
};

export default PfpGeneratorPage;
